/* eslint-disable @typescript-eslint/no-shadow */
import { toDimensionScore } from '@upright/common/model/output/scores';
import { FinalImpactScoreType, ImpactScoresByIVV } from '@upright/common/model/output/scores/types';
import { Dimension } from '@upright/common/model/types';
import { Bounds, CarouselLayout, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { sideToValence, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { NetImpactLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import { getMaxX } from '@upright/ui/common/upright/model/output/scores/net-impact-chart/horizontal-scale';

export interface DimensionBarsLayout {
  /**
   * Area available for bar
   */
  dimensionBarRangeArea: (side: Side, dimension: Dimension) => Bounds;

  /**
   * Area that bar will take
   */
  dimensionBarArea: (side: Side, dimension: Dimension) => Bounds;

  /**
   * Area reserved for bar label
   */
  dimensionBarLabelArea: (side: Side, dimension: Dimension) => Bounds;

  /**
   * Area taken by bar and label
   */
  dimensionBarAndLabelArea: (side: Side, dimension: Dimension) => Bounds;

  /**
   * Area of the ghost-baseline tail appended after the bar (see
   * `NetImpactLensData.ghostScores`); undefined when there is no ghost data
   * or the tail would be sub-pixel.
   */
  dimensionGhostTailArea: (side: Side, dimension: Dimension) => Bounds | undefined;

  maxX: number;
  spaceForBarLabel: number;
  marginForBar: number;

  // TODO: should not include NI lens-specific stuff here
  selectionState?: NetImpactLensSelectionState;
}

export function dimensionBarsLayout(
  base: CarouselLayout,
  data: ImpactScoresByIVV,
  scoreUnit?: FinalImpactScoreType,
  // Ghost-baseline scores (`NetImpactLensData.ghostScores`). Every call site
  // constructing this layout for the same profile MUST pass the same value —
  // it participates in `maxX`, so bars, tails, and labels would disagree on
  // the x-scale otherwise.
  ghostData?: ImpactScoresByIVV,
): DimensionBarsLayout {
  function getSpaceForBarLabel() {
    if (scoreUnit === FinalImpactScoreType.DOLLARS_PER_YEAR) {
      return 55;
    }
    return 45;
  }

  const spaceForBarLabel = getSpaceForBarLabel();
  const marginForBar = 4;
  // Ghost baselines are >= the solid scores, so they must participate in the
  // scale or their tails would overflow the bar range area.
  const maxScore = ghostData ? Math.max(getMaxX(data), getMaxX(ghostData)) : getMaxX(data);

  function dimensionBarRangeArea(side: Side, dimension: Dimension) {
    const da = base.dimensionHeadDataArea(side, dimension);

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = da.x1 + marginForBar;
        const x2 = da.x2 - spaceForBarLabel;
        return {
          x1,
          x2,
        };
      } else {
        const x2 = da.x2 - marginForBar;
        const x1 = da.x1 + spaceForBarLabel;
        return {
          x1,
          x2,
        };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({
      x1,
      x2,
      y1: da.y1,
      y2: da.y2,
    });
  }

  function dimensionBarArea(side: Side, dimension: Dimension) {
    const ra = dimensionBarRangeArea(side, dimension);

    function getXValues() {
      const score = toDimensionScore(data, dimension, sideToValence(side));
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (score === undefined) {
        return { x1: ra.x1, x2: ra.x2 };
      }
      if (side === Side.RIGHT) {
        const x1 = ra.x1;
        const x2 = ra.x1 + (ra.width * score) / maxScore;
        return { x1, x2 };
      } else {
        const x2 = ra.x2;
        const x1 = ra.x2 - (ra.width * score) / maxScore;
        return { x1, x2 };
      }
    }

    const barHeight = ra.height;
    const y1 = ra.cy - barHeight / 2;
    const y2 = y1 + barHeight;
    const { x1, x2 } = getXValues();
    return toBounds({ x1, x2, y1, y2 });
  }

  function dimensionGhostTailArea(side: Side, dimension: Dimension): Bounds | undefined {
    if (!ghostData) {
      return undefined;
    }
    const ra = dimensionBarRangeArea(side, dimension);
    const barArea = dimensionBarArea(side, dimension);
    const ghostScore = toDimensionScore(ghostData, dimension, sideToValence(side));
    const ghostWidth = (ra.width * ghostScore) / maxScore;
    // 2px gap between the solid bar and its tail (mirrors the stacked-bar
    // ghost in the multi-series chart); the 1px minimum swallows float noise
    // between the baseline and recomputed scores so unaffected bars never
    // grow phantom tails.
    const gap = barArea.width > 0 ? 2 : 0;
    const tailWidth = ghostWidth - barArea.width - gap;
    if (tailWidth < 1) {
      return undefined;
    }
    const { x1, x2 } =
      side === Side.RIGHT
        ? { x1: barArea.x2 + gap, x2: barArea.x2 + gap + tailWidth }
        : { x1: barArea.x1 - gap - tailWidth, x2: barArea.x1 - gap };
    return toBounds({ x1, x2, y1: barArea.y1, y2: barArea.y2 });
  }

  function dimensionBarLabelArea(side: Side, dimension: Dimension) {
    const da = dimensionBarRangeArea(side, dimension);

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = da.x2;
        const x2 = da.x2 + spaceForBarLabel;
        return {
          x1,
          x2,
        };
      } else {
        const x1 = da.x1 - spaceForBarLabel;
        const x2 = da.x1;
        return {
          x1,
          x2,
        };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({
      x1: x1,
      x2: x2,
      y1: da.y1,
      y2: da.y2,
    });
  }

  function dimensionBarAndLabelArea(side: Side, dimension: Dimension) {
    const barArea = dimensionBarArea(side, dimension);
    // The data label hangs off this area's outer edge, so a ghost tail must
    // extend it — otherwise the label would be drawn on top of the tail.
    const tailArea = dimensionGhostTailArea(side, dimension);
    const x1 = side === Side.RIGHT ? barArea.x1 : (tailArea?.x1 ?? barArea.x1);
    const x2 = side === Side.RIGHT ? (tailArea?.x2 ?? barArea.x2) : barArea.x2;
    const y1 = barArea.y1;
    const y2 = barArea.y2;
    return toBounds({ x1, x2, y1, y2 });
  }

  return {
    dimensionBarRangeArea,
    dimensionBarArea,
    dimensionBarLabelArea,
    dimensionBarAndLabelArea,
    dimensionGhostTailArea,
    maxX: maxScore,
    spaceForBarLabel,
    marginForBar,
  };
}
